import React, { useState } from 'react';
import { useCountdown } from '../hooks/useCountdown';
import { useEnroll } from '../context/EnrollContext';
import { BATCH_TARGET } from '../data/courseData';
import { submitEnrollment } from '../utils/submitEnroll';
import styles from './Hero.module.css';

export default function Hero() {
  const cd = useCountdown(BATCH_TARGET);
  const { open: openEnroll } = useEnroll();
  const [form, setForm]           = useState({ name: '', phone: '', email: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading,   setLoading]   = useState(false);
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) return;
    setLoading(true);
    setFormError('');
    try {
      await submitEnrollment({ ...form, source: 'hero' });
      setSubmitted(true);
    } catch (err) {
      setFormError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className={styles.hero} id="top" aria-label="Performance Marketing Course – Enroll Now">
      {/* Background orbs */}
      <div className={styles.orb1} />
      <div className={styles.orb2} />
      <div className={styles.grid} />

      <div className={styles.container}>
        <div className={styles.left}>
          {/* Eyebrow tags */}
          <div className={styles.eyebrow}>
            <span className={`${styles.tag} ${styles.tagAccent}`}>Performance Marketing</span>
            <span className={styles.tag}>Beginners Welcome</span>
            <span className={styles.liveDot}>
              <span className={styles.pulse} />
              Enrolling Now
            </span>
          </div>

          <h1 className={styles.headline}>
            Run Real Ads.<br />
            Get <span className={styles.accent}>Measurable</span><br />
            Results.
          </h1>

          <p className={styles.subheadline}>
            Master Meta Ads & Google Ads from scratch in Pune — with live campaigns,
            hands-on projects, and guidance from an active digital marketer.
            No experience needed. Online batches available across India.
          </p>

          <div className={styles.trust}>
            {[
              { icon: '⭐', text: '4.8/5 rating · 500+ students' },
              { icon: '🎓', text: 'Certificate on completion' },
              { icon: '💼', text: 'Career & freelance support' },
            ].map(t => (
              <div className={styles.trustItem} key={t.text}>
                <span className={styles.trustIcon}>{t.icon}</span>
                {t.text}
              </div>
            ))}
          </div>

          <div className={styles.actions}>
            <a href="#" className={styles.btnPrimary} onClick={e => { e.preventDefault(); openEnroll(); }}>
              Reserve My Seat <span className={styles.arrow}>→</span>
            </a>
            <a href="#curriculum" className={styles.btnGhost}>
              View Curriculum
            </a>
          </div>
          <p className={styles.microcopy}>No spam. No obligation. Just honest course info.</p>
        </div>

        {/* Enroll Card */}
        <div className={styles.right} id="enroll">
          <div className={styles.card}>
            <div className={styles.cardTop} />
            <span className={styles.cardBadge}>🎯 Next Batch — Filling Fast</span>
            <h3 className={styles.cardTitle}>Claim Your Seat Now</h3>
            <p className={styles.cardSub}>Get a free counselling call + detailed curriculum PDF</p>

            {/* Countdown */}
            <div className={styles.countdownSection}>
              <p className={styles.countdownLabel}>Batch starts in</p>
              <div className={styles.countdown}>
                {[
                  { val: cd.days,    lbl: 'Days' },
                  { val: cd.hours,   lbl: 'Hrs' },
                  { val: cd.minutes, lbl: 'Min' },
                  { val: cd.seconds, lbl: 'Sec' },
                ].map(u => (
                  <div className={styles.cdUnit} key={u.lbl}>
                    <span className={styles.cdNum}>{u.val}</span>
                    <span className={styles.cdLbl}>{u.lbl}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Seats bar */}
            <div className={styles.seatsBar}>
              <div className={styles.seatsLabel}>
                <span>Seats Filling</span>
                <strong>78% Full</strong>
              </div>
              <div className={styles.barTrack}>
                <div className={styles.barFill} />
              </div>
            </div>

            {/* Form */}
            {submitted ? (
              <div className={styles.successMsg}>
                <span className={styles.successIcon}>✅</span>
                <strong>You're on the list!</strong>
                <p>We'll call you within 4 hours to confirm your seat.</p>
              </div>
            ) : (
              <form className={styles.form} onSubmit={handleSubmit} aria-label="Enroll in Performance Marketing Course">
                <input
                  type="text"
                  className={styles.input}
                  placeholder="Your Full Name *"
                  aria-label="Full Name"
                  value={form.name}
                  onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                  required
                />
                <input
                  type="tel"
                  className={styles.input}
                  placeholder="Phone / WhatsApp Number *"
                  aria-label="Phone or WhatsApp Number"
                  value={form.phone}
                  onChange={e => setForm(p => ({ ...p, phone: e.target.value }))}
                  required
                />
                <input
                  type="email"
                  className={styles.input}
                  placeholder="Email Address (optional)"
                  aria-label="Email Address"
                  value={form.email}
                  onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                />
                <button type="submit" className={styles.enrollBtn} disabled={loading}>
                  {loading ? 'Submitting…' : 'Book My Free Counselling Call 📞'}
                </button>
                {formError && <p className={styles.formError}>{formError}</p>}
                <p className={styles.privacy}>🔒 Your info is private. No spam, ever.</p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
